import React, { useEffect, useRef, useState } from "react";
import { ChatSummary } from "../../../api/admin/types";
import { AgentMessage } from "./types";
import ChatMessage from "./ChatMessage";
import ChatSidebar from "./ChatSidebar";

interface Props {
  messages: AgentMessage[];
  isThinking: boolean;
  onSend: (text: string) => void;
  onClose: () => void;

  chats: ChatSummary[];
  chatsLoading: boolean;
  selectedChatId: string;
  onSelectChat: (chatId: string) => void;
  onNewChat: () => void;
  onDeleteChat: (chatId: string) => void;
}

const SUGGESTIONS = [
  "Which agencies dropped the most last month?",
  "Show me weekly task volume across all agencies for April.",
  "Compare this month vs last month overall.",
];

const ChatPanel: React.FC<Props> = ({
  messages,
  isThinking,
  onSend,
  onClose,
  chats,
  chatsLoading,
  selectedChatId,
  onSelectChat,
  onNewChat,
  onDeleteChat,
}) => {
  const [input, setInput] = useState("");
  const scrollerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    scrollerRef.current?.scrollTo({
      top: scrollerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, isThinking]);

  // Focus the input when the panel mounts AND each time the user switches to
  // a different chat (so they can immediately keep typing).
  useEffect(() => {
    textareaRef.current?.focus();
  }, [selectedChatId]);

  const submit = (text: string) => {
    if (!text.trim() || isThinking) return;
    onSend(text);
    setInput("");
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        height: "100%",
        background: "#fff",
        borderRadius: 16,
        overflow: "hidden",
      }}
    >
      <ChatSidebar
        chats={chats}
        loading={chatsLoading}
        selectedChatId={selectedChatId}
        onSelect={onSelectChat}
        onNew={onNewChat}
        onDelete={onDeleteChat}
        isThinking={isThinking}
      />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
              Usage Assistant
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              Ask about agency or user usage. Charts appear inline.
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              color: "#6b7280",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              width: 32,
              height: 32,
              fontSize: 18,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        <div
          ref={scrollerRef}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
            background: "#fafafa",
          }}
        >
        {messages.length === 0 ? (
          <div
            style={{
              margin: "auto",
              maxWidth: 480,
              textAlign: "center",
              color: "#6b7280",
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 8 }}>✦</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
              How can I help?
            </div>
            <div style={{ fontSize: 13, marginBottom: 18 }}>
              I can query usage, compare periods, and chart the result.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => submit(s)}
                  disabled={isThinking}
                  style={{
                    textAlign: "left",
                    padding: "10px 14px",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    background: "#fff",
                    fontSize: 13,
                    color: "#374151",
                    cursor: isThinking ? "not-allowed" : "pointer",
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isThinking) e.currentTarget.style.background = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => <ChatMessage key={i} message={m} />)
        )}
        {isThinking ? <ThinkingIndicator /> : null}
      </div>

      <div
        style={{
          borderTop: "1px solid #f1f5f9",
          padding: "12px 16px",
          background: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-end",
            border: "1px solid #d1d5db",
            borderRadius: 12,
            padding: "8px 8px 8px 14px",
            background: "#fff",
            transition: "border-color 0.15s, box-shadow 0.15s",
          }}
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder={isThinking ? "Working…" : "Ask a question…"}
            rows={1}
            disabled={isThinking}
            style={{
              flex: 1,
              resize: "none",
              border: "none",
              outline: "none",
              padding: "6px 0",
              fontSize: 14,
              fontFamily: "inherit",
              lineHeight: 1.45,
              background: "transparent",
              minHeight: 24,
              maxHeight: 160,
            }}
          />
          <button
            onClick={() => submit(input)}
            disabled={!input.trim() || isThinking}
            aria-label="Send"
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: !input.trim() || isThinking ? "#cbd5e1" : "#3949ab",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: !input.trim() || isThinking ? "not-allowed" : "pointer",
              transition: "background 0.15s",
            }}
          >
            Send
          </button>
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6, paddingLeft: 4 }}>
          Enter to send, Shift+Enter for newline.
        </div>
      </div>
      </div>
    </div>
  );
};

const ThinkingIndicator: React.FC = () => (
  <div
    style={{
      alignSelf: "flex-start",
      background: "#f3f4f6",
      borderRadius: "14px 14px 14px 4px",
      padding: "10px 14px",
      display: "flex",
      gap: 6,
      alignItems: "center",
    }}
  >
    <style>{`
      @keyframes ai-dot {
        0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
        40% { opacity: 1; transform: translateY(-2px); }
      }
      @keyframes ai-chip-spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "#6b7280",
          display: "inline-block",
          animation: `ai-dot 1.2s infinite ${i * 0.2}s ease-in-out`,
        }}
      />
    ))}
  </div>
);

export default ChatPanel;
